import { ref } from 'vue'
import { useI18n } from 'vue-i18n'

import useLogger from '@/services/loggerService'
import useBlProviders from '../providers/useBlProvider'

import MachinesDt from '@/services/dataTransferModules/machinesDt'

import useMachineFuelTanks from './useMachineFuelTanks' //need for copy :TODO: remove after adding copy endpoint
import useMachineBrigade from './useMachineBrigade' //need for copy
import { getMachineTitle } from '@/helpers/entities/machines/machinesHelpers'
import { fetchAndUploadFile } from '@/helpers/fileHelper'
import { clearObjectServiceAttrs } from '@/helpers/utilsHelper'
import useMachineTypes from '@/composables/staticData/useMachineTypes'
import useMachineConditionStatuses from '@/composables/staticData/useMachineConditionStatuses'

import useDataEngineNullableObjects from '@/composables/staticData/useDataEngineNullableObjects'

export default function useMachines(params = {}) {
	const { t: $t } = useI18n()

	const machinesDt = new MachinesDt()

	const { machineTypesObj } = useMachineTypes()
	const { machineConditionStatuses } = useMachineConditionStatuses()
	const { dataEngineNullableObjects } = useDataEngineNullableObjects()
	const { machineTypeIfNotValueObj } = dataEngineNullableObjects.value

	const logger = useLogger()
	const { createMachineFuelTank } = useMachineFuelTanks() //need for copy
	const { createMachineBrigade } = useMachineBrigade() //need for copy

	const postProcessEntityCallback = (machine) => {
		const machineType = machineTypesObj.value[machine.machine_type] ?? machineTypeIfNotValueObj
		return {
			...machine,
			__ao__title: getMachineTitle(machine),
			__ao__machine_type: machineType,
			__ao__condition_status: machineConditionStatuses.value[machine.condition_status],
		}
	}

	const {
		selectedCompanyId,
		getPreparedQueryForDt,

		loadEntityFlags: flags,

		getEntities: getFulledMachines,

		basedEntities: basedMachinesList,
		getBasedEntities: getBasedMachines,

		hashedBasedEntities: hashedBasedMachines,
		getHashedBasedEntities: getHashedBasedMachines,

		extendedEntities: extendedMachinesList,
		getExtendedEntities: getExtendedMachines,

		hashedExtendedEntities: hashedExtendedMachines,
		getHashedExtendedEntities: getHashedExtendedMachines,

		createEntity,

		entity: singleLoadedMachine,
		getEntity: loadSingleMachineById,

		entityPreview: machinePreview,
		getEntityPreview: getPreviewOfMachine,

		entityPopup: machinePopup,
		getEntityPopup: getMachinePopup,

		patchEntity: editMachine,
		deleteEntity: deleteMachine,
	} = useBlProviders(
		machinesDt,
		Object.assign(
			{
				postProcessEntityCallback,

				showErrorNotificationForLoadingAllEntities: true,
				errorNotificationTextForLoadingAllEntities: $t('Error while loading machinery'),

				showErrorNotificationForCreatingEntity: true,
				errorNotificationTextForCreatingEntity: $t('Error while creating machinery'),

				showErrorNotificationForLoadingOneEntity: true,

				showErrorNotificationForUpdatingEntity: true,
				errorNotificationTextForUpdatingEntity: $t('Error while editing machinery'),

				showErrorNotificationForDeletingEntity: true,
				errorNotificationTextForDeletingEntity: $t('Error while deleting machinery'),
			},
			params,
		),
	)

	const machinesList = ref([])
	const copiedMachine = ref({})
	const sensorsValues = ref({})
	const rawGPSParams = ref({})

	const defaults = {
		creatingMachine: {
			brand: '',
			model: '',
			entity: null,
			property: '',
			machine_type: '',
		},
	}

	const loadMachines = async (query = {}) => {
		flags.isLoading = true

		try {
			const res = await machinesDt.getAvailableMachines(selectedCompanyId.value, query)

			machinesList.value = res.map(postProcessEntityCallback)
			flags.isLoading = false

			return machinesList.value
		} catch (error) {
			logger.debug.error(error)

			flags.isLoading = false
			logger.ui.pushErrorNotification({
				timeout: 3000,
				text: $t('Error while loading machinery'),
			})
		}
	}

	const getMachinesForMapLayer = async (query) => {
		flags.isLoading = true

		try {
			const res = await machinesDt.getAvailableMachines(selectedCompanyId.value, query)

			flags.isLoading = false
			machinesList.value = res
				.map(postProcessEntityCallback)
				.filter((item) => !!item.device_id && item.under_monitoring)

			return machinesList.value
		} catch (error) {
			logger.debug.error(error)

			flags.isLoading = false
			logger.ui.pushErrorNotification({
				timeout: 3000,
				text: $t('Error while loading machinery'),
			})
		}
	}

	const createMachine = (payload) => {
		return createEntity({
			...payload,
			type: 'AL',
			company: selectedCompanyId.value,
		})
	}

	const copyMachine = async (machineId) => {
		// eslint-disable-next-line no-unused-vars
		const { id, ...machineToCopy } = await loadSingleMachineById(machineId)

		const postBody = Object.keys(defaults.creatingMachine).reduce((body, field) => {
			body[field] = machineToCopy[field]
			return body
		}, {})

		const newMachine = await createMachine({
			...postBody,
			brand: `${machineToCopy.brand} (${$t('copy [noun]')})`,
		})

		const patchBody = {
			...clearObjectServiceAttrs(machineToCopy),
			brand: newMachine.brand,
			inventory_date: null,
			inventory_executor: null,
		}

		if (machineToCopy.icon) {
			const uploadedFile = await fetchAndUploadFile(machineToCopy.icon)
			if (uploadedFile.id) {
				patchBody.icon_file_id = uploadedFile.id
			}
		}

		copiedMachine.value = await editMachine(newMachine.id, patchBody)

		const createBrigades = (machineToCopy.machine_brigade ?? []).map(({ staff, role }) => {
			return createMachineBrigade(copiedMachine.value.id, {
				staff,
				role,
			})
		})

		const createTanks = (machineToCopy.fuel_tanks ?? []).map(({ type, volume }) => {
			return createMachineFuelTank(copiedMachine.value.id, {
				type,
				volume,
			})
		})

		await Promise.all([...createBrigades, ...createTanks])

		return copiedMachine.value
	}

	const getSensorsValues = (machineId, payload) => {
		const query = getPreparedQueryForDt(payload, 'time_from', 'time_to')

		return new Promise((resolve, reject) => {
			flags.isLoading = true

			machinesDt
				.getSensorsValues(selectedCompanyId.value, machineId, query)
				.then((res) => {
					sensorsValues.value = res
					flags.isLoading = false

					resolve(res)
				})
				.catch((err) => {
					flags.isLoading = false

					logger.ui.pushErrorNotification({
						timeout: 3000,
						text: $t('Error while loading machinery'),
					})

					reject(err)
				})
		})
	}

	const getRawGPSParams = (machineId, payload) => {
		const query = getPreparedQueryForDt(payload, 'time_from', 'time_to')

		return new Promise((resolve, reject) => {
			flags.isLoading = true

			machinesDt
				.getRawGPSParams(selectedCompanyId.value, machineId, query)
				.then((res) => {
					rawGPSParams.value = res
					flags.isLoading = false

					resolve(res)
				})
				.catch((err) => {
					flags.isLoading = false

					logger.ui.pushErrorNotification({
						timeout: 3000,
						text: $t('Error while loading machinery'),
					})

					reject(err)
				})
		})
	}

	return {
		flags,
		defaults,

		machinesList,
		basedMachinesList,
		extendedMachinesList,
		machinePopup,
		machinePreview,
		singleLoadedMachine,

		loadMachines,
		getMachinesForMapLayer,
		getFulledMachines,
		getBasedMachines,
		getExtendedMachines,
		copiedMachine,

		hashedBasedMachines,
		getHashedBasedMachines,

		hashedExtendedMachines,
		getHashedExtendedMachines,

		loadSingleMachineById,
		createMachine,
		deleteMachine,
		editMachine,

		getPreviewOfMachine,
		getMachinePopup,
		copyMachine,

		sensorsValues,
		getSensorsValues,

		rawGPSParams,
		getRawGPSParams,
	}
}
